import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Checkbox } from "../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "../../ui/dropdown-menu";
import {
  MoreVertical, RefreshCw, GitMerge, Loader2, Building2, Pencil, FileText,
  NotebookPen, UserCog, Ban, Trash2, Search, Rows3, AlertTriangle, CalendarClock,
} from "lucide-react";
import { formatDate } from "../../../lib/date";
import AddToNurture from "../AddToNurture";
import { STATUS_META, STATUS_ORDER, SignupBadge, StatusCell, stageFor, isQuoteDue, overdueDays } from "./prospectConstants";
import { useSubStatuses } from "./subStatus";
import { AskAssistant } from "../AskAssistant";
import { OverseasBadge } from "../OverseasBadge";

const GRID = { gridTemplateColumns: "30px minmax(230px,1.6fr) 150px 168px 148px 108px 112px 104px", columnGap: "10px" };
const DENSITY_H = { Compact: 34, Cozy: 46, Comfortable: 46, Roomy: 56 };

function StageCell({ status }) {
  const s = stageFor(status);
  if (!s) return <span className="mono text-[11px] text-[#B5AFA3]">—</span>;
  const pct = Math.round((s.step / s.total) * 100);
  const complete = s.step >= s.total;
  return (
    <div className="min-w-0">
      <div className="flex items-center gap-1.5">
        <span className="text-[11.5px] font-semibold text-[#1A1A1A] truncate">{s.label}</span>
        <span className="mono text-[11px] text-[#59554D] shrink-0">{s.step}/{s.total}</span>
      </div>
      <div className="mt-1 rounded-full" style={{ width: 86, height: 3, background: "#F4F2ED" }}>
        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: complete ? "#0E6B3F" : "#8A857A" }} />
      </div>
    </div>
  );
}

function NextStepCell({ date }) {
  if (!date) return <span className="mono text-[11.5px] text-[#B5AFA3]">—</span>;
  const late = overdueDays(date);
  if (late > 0) {
    return (
      <div className="text-right">
        <div className="mono text-[11.5px]" style={{ color: "#9A3200", fontWeight: 500 }}>{formatDate(date)}</div>
        <div className="mono text-[10px]" style={{ color: "#9A3200" }}>+{late}d late</div>
      </div>
    );
  }
  return <span className="mono text-[11.5px] text-[#1A1A1A]">{formatDate(date)}</span>;
}

function RowMenu({ p, openEdit, openDetail, setConfirmDelete, openFollowUp }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="icon" variant="ghost" className="h-8 w-8 text-[#59554D] hover:bg-[#F4F2ED]"
          onClick={(e) => e.stopPropagation()} title="Actions" data-testid={`sales-rowmenu-${p.id}`}>
          <MoreVertical className="w-4 h-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[252px] bg-[#FBFAF7] border-[#DAD5CA] rounded-xl p-1.5"
        onClick={(e) => e.stopPropagation()} style={{ boxShadow: "var(--shadow-pop)" }}>
        <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => openEdit(p)} data-testid={`sales-menu-edit-${p.id}`}>
          <Pencil className="w-[15px] h-[15px] text-[#6B6660]" /> Edit prospect
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => openDetail(p)} data-testid={`sales-menu-quote-${p.id}`}>
          <FileText className="w-[15px] h-[15px] text-[#6B6660]" /> Create quote
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => openDetail(p, "activity")} data-testid={`sales-menu-log-${p.id}`}>
          <NotebookPen className="w-[15px] h-[15px] text-[#6B6660]" /> Log activity
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => openFollowUp(p)} data-testid={`sales-menu-followup-${p.id}`}>
          <CalendarClock className="w-[15px] h-[15px] text-[#6B6660]" /> Create follow-up
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-lg text-[13px] font-semibold text-[#1A1A1A] gap-2" onClick={() => openEdit(p)} data-testid={`sales-menu-assign-${p.id}`}>
          <UserCog className="w-[15px] h-[15px] text-[#6B6660]" /> Assign rep
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-[#E6E2D9]" />
        <DropdownMenuItem className="rounded-lg text-[13px] font-semibold gap-2" style={{ color: "#8E1212" }} onClick={() => openEdit(p)} data-testid={`sales-menu-disqualify-${p.id}`}>
          <Ban className="w-[15px] h-[15px]" style={{ color: "#8E1212" }} /> Disqualify
        </DropdownMenuItem>
        <DropdownMenuItem className="rounded-lg items-start gap-2 py-2" style={{ color: "#8E1212" }} onClick={() => setConfirmDelete(p)} data-testid={`sales-menu-delete-${p.id}`}>
          <Trash2 className="w-[15px] h-[15px] mt-0.5" style={{ color: "#8E1212" }} />
          <span>
            <span className="text-[13px] font-semibold block">Delete prospect</span>
            <span className="text-[11.5px] font-normal text-[#59554D]">Removes the record and its activity history. Cannot be undone.</span>
          </span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function ProspectList({ ctx }) {
  const {
    loading, filtered, total, filtersActive, selectedIds, reps,
    bulkRep, setBulkRep, bulkStatus, setBulkStatus, applyingBulk, applyBulk, openMergeDialog, setSelectedIds,
    allVisibleSelected, toggleSelectAllVisible, toggleSelect,
    cycleDensity, fetchProspects, DENSITY_LABEL, SortHead,
    openDetail, openEdit, setConfirmDelete, openFollowUp, hasMore, loadingMore, sentinelRef,
    setCategoryFilter, setSourceFilter, setStatusFilter, setRepFilter, setQuery,
    query, categoryFilter, sourceFilter, sortBy, setSortBy, categories, sourceTypes,
    workload, repFilter, reviewOnly, setReviewOnly, stats, signupFilter, setSignupFilter,
  } = ctx;

  const rowH = DENSITY_H[DENSITY_LABEL] || 46;
  const { map: subMap } = useSubStatuses();
  const clearAll = () => { setCategoryFilter("all"); setSourceFilter("all"); setStatusFilter("all"); setRepFilter("all"); setReviewOnly && setReviewOnly(false); setSignupFilter && setSignupFilter("all"); setQuery(""); };

  const HeaderBand = (
    <div className="grid px-3.5 h-9 items-center bg-[#F6F4EF] border-b border-[#DAD5CA]" style={GRID}>
      <div className="flex items-center">
        <Checkbox checked={allVisibleSelected} onCheckedChange={toggleSelectAllVisible}
          className="border-[#C9C3B6] data-[state=checked]:bg-[#FFD108] data-[state=checked]:border-[#8A6D00]" data-testid="sales-select-all" />
      </div>
      <SortHead label="Company" k="company_name" />
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium">Stage</div>
      <SortHead label="Status" k="status" />
      <SortHead label="Rep" k="assigned_rep" />
      <div className="flex justify-end"><SortHead label="Last activity" k="last_activity_date" right /></div>
      <div className="flex justify-end"><SortHead label="Next step" k="next_action_date" right /></div>
      <div className="text-[10px] uppercase tracking-[0.1em] text-[#6B6660] font-medium text-right">Actions</div>
    </div>
  );

  return (
    <Card className="bg-white border-[#DAD5CA] rounded-xl overflow-hidden p-0" style={{ boxShadow: "var(--shadow-card)" }}>
      <CardContent className="p-0">
        {/* search + filters band */}
        <div className="flex items-center gap-2.5 flex-wrap px-3.5 py-3 border-b border-[#E6E2D9]">
          <div className="relative flex-1 min-w-[280px] max-w-[560px]">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A857A]" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Company, contact, suburb, category or source"
              className="pl-10 pr-24 bg-white border-[#C9C3B6] text-[#1A1A1A] rounded-[10px]" style={{ height: 42 }} data-testid="sales-search" />
            <span className="mono text-[11px] text-[#8A857A] absolute right-3.5 top-1/2 -translate-y-1/2" data-testid="sales-search-count">{filtered.length} of {stats?.total ?? total}</span>
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[164px] bg-white border-[#C9C3B6] text-[#1A1A1A] rounded-[10px]" style={{ height: 42 }} data-testid="sales-filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DAD5CA]"><SelectItem value="all">All categories</SelectItem>{(categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-[150px] bg-white border-[#C9C3B6] text-[#1A1A1A] rounded-[10px]" style={{ height: 42 }} data-testid="sales-filter-source"><SelectValue placeholder="All sources" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DAD5CA]"><SelectItem value="all">All sources</SelectItem>{(sourceTypes || []).map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[186px] bg-white border-[#C9C3B6] text-[#1A1A1A] rounded-[10px]" style={{ height: 42 }} data-testid="sales-sort"><SelectValue placeholder="Sort" /></SelectTrigger>
            <SelectContent className="bg-white border-[#DAD5CA]">
              <SelectItem value="last_activity">Most recent activity</SelectItem>
              <SelectItem value="oldest_activity">Oldest activity</SelectItem>
              <SelectItem value="inbound_first">Inbound / demos first</SelectItem>
              <SelectItem value="newest">Newest added</SelectItem>
              <SelectItem value="company">Company A–Z</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* workload band */}
        <div className="flex items-center gap-2 flex-wrap px-3.5 py-2.5 border-b border-[#E6E2D9] bg-[#FBFAF7]" data-testid="sales-workload">
          <span className="text-[10px] uppercase tracking-[0.12em] text-[#8A857A] font-bold mr-1">Workload</span>
          {(workload?.reps || []).map((w) => (
            <button key={w.rep} onClick={() => setRepFilter(repFilter === w.rep ? "all" : w.rep)} data-testid={`sales-workload-${w.rep}`}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${repFilter === w.rep ? "border-[#E8D48A] bg-[#FFF8D6] text-[#8A6D00]" : "border-[#C9C3B6] bg-white text-[#59554D] hover:bg-[#F4F2ED]"}`}>
              {w.rep}<span className="mono text-[11px] text-[#1A1A1A]">{w.count}</span>
            </button>
          ))}
          <button onClick={() => setRepFilter(repFilter === "__unassigned__" ? "all" : "__unassigned__")} data-testid="sales-workload-unassigned"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${repFilter === "__unassigned__" ? "border-[#E8D48A] bg-[#FFF8D6] text-[#8A6D00]" : "border-[#C9C3B6] bg-white text-[#59554D] hover:bg-[#F4F2ED]"}`}>
            Unassigned<span className="mono text-[11px] text-[#1A1A1A]">{workload?.unassigned ?? 0}</span>
          </button>
          <button onClick={() => setReviewOnly((v) => !v)} data-testid="sales-filter-needs-review" title="Website enquiries with missing details — please review"
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-[12px] font-semibold transition-colors ${reviewOnly ? "border-[#E8D48A] bg-[#FFF7D1] text-[#6B5600]" : "border-[#C9C3B6] bg-white text-[#59554D] hover:bg-[#F4F2ED]"}`}>
            <AlertTriangle className="w-3.5 h-3.5 text-[#C23A00]" /> Needs review
          </button>
          <div className="ml-auto flex items-center gap-2">
            {filtersActive && (
              <button onClick={clearAll} className="text-[12px] text-[#8A6D00] hover:underline" data-testid="sales-filter-clear">Clear filters</button>
            )}
            <button onClick={cycleDensity} className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.1em] font-bold text-[#59554D] hover:text-[#1A1A1A]" title="Cycle row density" data-testid="sales-density">
              <Rows3 className="w-4 h-4" /> {DENSITY_LABEL}
            </button>
          </div>
        </div>

        {/* ask band */}
        <div className="px-3.5 py-3 border-b border-[#E6E2D9]">
          <AskAssistant
            endpoint={`${process.env.REACT_APP_BACKEND_URL}/api/sales/prospects/ask`}
            title="Ask about your prospects" placeholder="e.g. which prospects completed a demo but have no quote?"
            example="which prospects completed a demo but have no quote?"
            metaFor={(c) => c.status || c.assigned_rep} testid="prospect-ask" />
        </div>

        {/* bulk bar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-2.5 flex-wrap bg-[#FFF8D6] border-b border-[#EFE0A0] px-3.5 py-2.5" data-testid="sales-bulk-toolbar">
              <span className="text-[12.5px] font-semibold text-[#1A1A1A]">{selectedIds.size} {selectedIds.size === 1 ? "prospect" : "prospects"} selected</span>
              <span className="w-px h-5 bg-[#EFE0A0]" />
              <Select value={bulkRep} onValueChange={setBulkRep}>
                <SelectTrigger className="w-[168px] bg-white border-[#DCCF95] text-[#1A1A1A] h-7 text-[12px]" data-testid="sales-bulk-rep"><SelectValue placeholder="Assign rep" /></SelectTrigger>
                <SelectContent className="bg-white border-[#DAD5CA]">
                  <SelectItem value="none">Assign rep…</SelectItem>
                  <SelectItem value="unassigned">Clear rep (unassign)</SelectItem>
                  {reps.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger className="w-[150px] bg-white border-[#DCCF95] text-[#1A1A1A] h-7 text-[12px]" data-testid="sales-bulk-status"><SelectValue placeholder="Set stage" /></SelectTrigger>
                <SelectContent className="bg-white border-[#DAD5CA]">
                  <SelectItem value="none">Set stage…</SelectItem>
                  {STATUS_ORDER.map((s) => <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button size="sm" onClick={applyBulk} disabled={applyingBulk || (bulkRep === "none" && bulkStatus === "none")}
                className="h-7 bg-white border border-[#DCCF95] hover:bg-[#FFFCEF] text-[#1A1A1A] text-[12px] font-semibold" data-testid="sales-bulk-apply">
                {applyingBulk && <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" />} Apply
              </Button>
              <button type="button" disabled className="h-7 px-3 rounded-[7px] text-[12px] font-semibold" data-testid="sales-bulk-email"
                title="Not available yet — bulk email endpoint is not live" style={{ background: "#E6E2D9", border: "1px solid #D3CEC2", color: "#B5AFA3", cursor: "default" }}>
                Bulk email
              </button>
              <Button size="sm" variant="outline" onClick={openMergeDialog} disabled={selectedIds.size < 2}
                className="h-7 border-[#DCCF95] bg-white hover:bg-[#FFFCEF] text-[#59554D] text-[12px]" data-testid="sales-bulk-merge">
                <GitMerge className="w-3.5 h-3.5 mr-1" /> Merge
              </Button>
              <button type="button" onClick={() => setSelectedIds(new Set())} className="ml-auto text-[12px] underline" style={{ color: "#8A6D00" }} data-testid="sales-bulk-clear">Clear</button>
            </div>
          )}

          {/* table */}
          <div className="overflow-x-auto">
            <div style={{ minWidth: 1200 }}>
              {HeaderBand}

              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="grid px-3.5 items-center border-b border-[#F0EDE6]" style={{ ...GRID, minHeight: rowH }}>
                    <div><div className="skeleton h-4 w-4 rounded" /></div>
                    <div className="skeleton h-3.5 w-40" /><div className="skeleton h-3 w-24" /><div className="skeleton h-3.5 w-20" />
                    <div className="skeleton h-3.5 w-24" /><div className="skeleton h-3 w-16 ml-auto" /><div className="skeleton h-3 w-16 ml-auto" />
                    <div className="skeleton h-7 w-7 rounded ml-auto" />
                  </div>
                ))
              ) : total === 0 && !filtersActive ? (
                <div className="px-5" style={{ padding: "56px 20px", textAlign: "center" }} data-testid="sales-empty">
                  <Building2 className="w-7 h-7 mx-auto text-[#8A857A] mb-3" />
                  <p className="text-[15px] font-semibold text-[#1A1A1A]">No prospects yet.</p>
                </div>
              ) : filtered.length === 0 ? (
                <div style={{ padding: "56px 20px", textAlign: "center" }} data-testid="sales-filtered-empty">
                  <p className="text-[15px] font-semibold text-[#1A1A1A]">No prospects match these filters.</p>
                  <Button variant="outline" onClick={clearAll} className="mt-4 border-[#C9C3B6] bg-white text-[#59554D] hover:bg-[#F4F2ED]" data-testid="sales-clear-filters-empty">Clear filters</Button>
                </div>
              ) : (
                filtered.map((p) => {
                  const sel = selectedIds.has(p.id);
                  const late = overdueDays(p.next_action_date) > 0;
                  const spine = late ? "inset 3px 0 0 #FF4F00" : sel ? "inset 3px 0 0 #FFD108" : "none";
                  return (
                    <div key={p.id}
                      className={`grid px-3.5 items-center border-b border-[#F0EDE6] cursor-pointer transition-colors ${sel ? "bg-[#FFF8D6]" : "hover:bg-[#FFFCEF]"}`}
                      style={{ ...GRID, minHeight: rowH, boxShadow: spine }}
                      onClick={() => openDetail(p)} data-testid={`sales-row-${p.id}`}>
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center">
                        <Checkbox checked={sel} onCheckedChange={() => toggleSelect(p.id)}
                          className="border-[#C9C3B6] data-[state=checked]:bg-[#FFD108] data-[state=checked]:border-[#8A6D00]" data-testid={`sales-select-${p.id}`} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="text-[13px] font-semibold text-[#1A1A1A] truncate">{p.company_name}</span>
                      <OverseasBadge currency={p.currency} country={p.country} testid={`prospect-overseas-${p.id}`} />
                          {isQuoteDue(p) && (
                            <span className="shrink-0 text-[10px] font-bold uppercase" data-testid={`sales-quotedue-${p.id}`}
                              style={{ background: "#FFEDE4", color: "#8F2E00", border: "1px solid #FFC9AE", borderRadius: 4, padding: "1px 5px" }}>Quote due</span>
                          )}
                          <SignupBadge prospect={p} />
                        </div>
                        <div className="text-[11.5px] text-[#59554D] truncate">
                          {[p.category, p.suburb, (p.source_label || p.source)].filter(Boolean).join(" · ") || <span className="text-[#B5AFA3]">—</span>}
                        </div>
                      </div>
                      <div><StageCell status={p.status} /></div>
                      <div data-testid={`sales-status-${p.id}`}>
                        <StatusCell status={p.status} />
                        {p.sub_status && subMap[p.sub_status] && (
                          <div className="text-[11px] text-[#8A857A] truncate mt-1" data-testid={`sales-substatus-${p.id}`}>{subMap[p.sub_status]}</div>
                        )}
                      </div>
                      <div className="text-[12.5px] truncate">
                        {p.assigned_rep ? <span className="text-[#1A1A1A]">{p.assigned_rep}</span> : <span className="italic text-[#8A857A]">Unassigned</span>}
                      </div>
                      <div className="text-right" data-testid={`sales-lastactivity-${p.id}`}>
                        {p.last_activity_date ? <span className="mono text-[11.5px] text-[#59554D]">{formatDate(p.last_activity_date)}</span> : <span className="mono text-[11.5px] text-[#B5AFA3]">—</span>}
                      </div>
                      <div className="text-right"><NextStepCell date={p.next_action_date} /></div>
                      <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
                        <RowMenu p={p} openEdit={openEdit} openDetail={openDetail} setConfirmDelete={setConfirmDelete} openFollowUp={openFollowUp} />
                      </div>
                    </div>
                  );
                })
              )}

              {!loading && filtered.length > 0 && (
                <div ref={sentinelRef} className="py-4 text-center" data-testid="sales-scroll-sentinel">
                  {loadingMore ? (
                    <span className="inline-flex items-center text-xs text-[#8A857A]"><Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading more…</span>
                  ) : hasMore ? (
                    <Button variant="ghost" size="sm" onClick={() => fetchProspects(false)} className="text-[#59554D] hover:text-[#1A1A1A]" data-testid="sales-load-more">Load more</Button>
                  ) : (
                    <span className="text-xs text-[#C9C3B6]">End of list</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* footer */}
          <div className="flex items-center justify-between gap-3 flex-wrap px-3.5 py-[11px] bg-[#FBFAF7] border-t border-[#E6E2D9]">
            <span className="mono text-[11.5px] text-[#59554D]">{loading ? "Loading…" : `${filtered.length} of ${total} prospects`}</span>
            <span className="text-[11px] text-[#8A857A]">Scroll to load more · page size 25</span>
          </div>
        </CardContent>
      </Card>
  );
}
